import apiClient from "./apiClient";
import { APP_COMPANY_NAME } from "../utils/constants";
import {
  clearStoredAuthSession,
  getStoredAuthSession,
  setStoredAuthSession,
} from "../utils/storage";
import {
  clearTokens,
  getRefreshToken,
  setAccessToken,
  setRefreshToken,
} from "../utils/token";

function normalizeUser(user) {
  return {
    ...user,
    fullName: user?.fullName || user?.name || "",
    company: user?.company || APP_COMPANY_NAME,
  };
}

function buildSession(payload) {
  const authData = payload?.data || payload || {};
  const dataUser = authData?.user || authData?.data?.user || {};
  const dataRoles = authData?.roles || dataUser?.roles || [];
  const dataPermissions = authData?.permissions || [];
  const tokenRoot = authData?.tokens || authData?.data?.tokens || authData;

  return {
    accessToken: tokenRoot?.accessToken || "",
    refreshToken: tokenRoot?.refreshToken || "",
    user: normalizeUser(dataUser),
    roles: dataRoles,
    permissions: dataPermissions,
  };
}

function persistSession(session) {
  setAccessToken(session.accessToken);
  setRefreshToken(session.refreshToken);
  setStoredAuthSession(session);
  return session;
}

const authService = {
  async login(credentials) {
    const response = await apiClient.post("/auth/login", {
      username: credentials.username.trim(),
      password: credentials.password,
    });

    return persistSession(buildSession(response.data));
  },

  async register(payload) {
    const response = await apiClient.post("/auth/register", {
      username: payload.username.trim(),
      fullName: payload.fullName.trim(),
      email: payload.email.trim().toLowerCase(),
      password: payload.password,
      roleId: payload.roleId || 2,
    });

    return persistSession(buildSession(response.data));
  },

  async refreshToken(tokenOverride) {
    const refreshToken = tokenOverride || getRefreshToken();
    const response = await apiClient.post("/auth/refresh", { refreshToken });
    const session = buildSession(response.data);
    setAccessToken(session.accessToken);
    if (session.refreshToken) {
      setRefreshToken(session.refreshToken);
    }
    return session;
  },

  logout() {
    clearTokens();
    clearStoredAuthSession();
  },

  getSession() {
    const session = getStoredAuthSession();

    if (!session?.accessToken || !session?.user) {
      return null;
    }

    setAccessToken(session.accessToken);
    if (session.refreshToken) {
      setRefreshToken(session.refreshToken);
    }
    return session;
  },

  updateSession(partialData) {
    const current = getStoredAuthSession();
    if (!current) return null;
    const next = { ...current, ...partialData };
    setStoredAuthSession(next);
    return next;
  },

  async forgotPassword(email) {
    return await apiClient.post("/auth/forgot-password", {
      email: email.trim().toLowerCase(),
    });
  },

  async verifyOtp(email, otp) {
    return await apiClient.post("/auth/verify-otp", {
      email: email.trim().toLowerCase(),
      otp: otp.trim(),
    });
  },

  async resetPassword(email, otp, newPassword) {
    return await apiClient.post("/auth/reset-password", {
      email: email.trim().toLowerCase(),
      otp: otp.trim(),
      newPassword,
    });
  },
};

export default authService;
